import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import type { BoardTimerCompletionActions } from '../../interfaces/BoardDetails';

const QUICK_TIMER_OPTIONS = [
  { label: '1 minute', seconds: 60 },
  { label: '5 minutes', seconds: 5 * 60 },
  { label: '10 minutes', seconds: 10 * 60 },
];

const CUSTOM_TIMER_OPTION = 'custom';
const TIMER_UNIT_SECONDS = {
  minutes: 60,
  hours: 60 * 60,
  days: 24 * 60 * 60,
} as const;

type TimerUnit = keyof typeof TIMER_UNIT_SECONDS;
type TimerCompletionActionValue = 'unchanged' | 'enabled' | 'disabled';

const TIMER_COMPLETION_ACTION_OPTIONS: Array<{
  key: keyof BoardTimerCompletionActions;
  label: string;
}> = [
  { key: 'participantUploadsEnabled', label: 'Adding cards' },
  { key: 'votingEnabled', label: 'Voting' },
  { key: 'commentsEnabled', label: 'Commenting' },
];

const getCompletionActionEnabledValue = (value: TimerCompletionActionValue) => {
  if (value === 'enabled') {
    return true;
  }

  if (value === 'disabled') {
    return false;
  }

  return null;
};

const getDefaultCompletionActionValues = (): Record<
  keyof BoardTimerCompletionActions,
  TimerCompletionActionValue
> => ({
  votingEnabled: 'unchanged',
  participantUploadsEnabled: 'unchanged',
  commentsEnabled: 'unchanged',
});

type TimerCompletionActionRowProps = {
  label: string;
  value: TimerCompletionActionValue;
  onChange: (value: TimerCompletionActionValue) => void;
};

const getTimerCompletionToggleSx = (value: TimerCompletionActionValue) => {
  if (value === 'enabled') {
    return {
      '&.Mui-selected': {
        color: '#176f38',
        backgroundColor: '#e6f4ea',
        '&:hover': {
          backgroundColor: '#d8eddf',
        },
      },
    };
  }

  if (value === 'disabled') {
    return {
      '&.Mui-selected': {
        color: '#a73434',
        backgroundColor: '#fbe7e7',
        '&:hover': {
          backgroundColor: '#f7d9d9',
        },
      },
    };
  }

  return {
    '&.Mui-selected': {
      color: '#4b5563',
      backgroundColor: '#f3f4f6',
      '&:hover': {
        backgroundColor: '#e8eaed',
      },
    },
  };
};

const TimerCompletionActionRow = ({ label, value, onChange }: TimerCompletionActionRowProps) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={1}
    alignItems={{ xs: 'stretch', sm: 'center' }}
    justifyContent="space-between"
  >
    <Box
      component="span"
      sx={{
        minWidth: 112,
        color: '#3c4043',
        fontSize: '0.875rem',
        fontWeight: 600,
      }}
    >
      {label}
    </Box>
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, nextValue: TimerCompletionActionValue | null) => {
        if (nextValue) {
          onChange(nextValue);
        }
      }}
      aria-label={`${label} timer completion action`}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        width: { xs: '100%', sm: 270 },
        '& .MuiToggleButtonGroup-grouped': {
          m: 0,
          minWidth: 0,
          px: 1,
          whiteSpace: 'nowrap',
          fontSize: '0.75rem',
          fontWeight: 700,
          textTransform: 'none',
          borderColor: '#d6d6d6',
        },
      }}
    >
      <ToggleButton
        value="unchanged"
        aria-label={`${label}: leave unchanged`}
        sx={getTimerCompletionToggleSx('unchanged')}
      >
        No change
      </ToggleButton>
      <ToggleButton
        value="enabled"
        aria-label={`${label}: enable`}
        sx={getTimerCompletionToggleSx('enabled')}
      >
        Enable
      </ToggleButton>
      <ToggleButton
        value="disabled"
        aria-label={`${label}: disable`}
        sx={getTimerCompletionToggleSx('disabled')}
      >
        Disable
      </ToggleButton>
    </ToggleButtonGroup>
  </Stack>
);

type BoardTimerSetupPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  hasVoting?: boolean;
  onClose: () => void;
  onBeforeStart?: () => void;
  onStartTimer: (
    durationSeconds: number,
    completionActions: BoardTimerCompletionActions
  ) => Promise<void>;
};

const BoardTimerSetupPopover = ({
  open,
  anchorEl,
  hasVoting = true,
  onClose,
  onBeforeStart,
  onStartTimer,
}: BoardTimerSetupPopoverProps) => {
  const [selectedTimerDuration, setSelectedTimerDuration] = useState('300');
  const [customTimerValue, setCustomTimerValue] = useState('15');
  const [customTimerUnit, setCustomTimerUnit] = useState<TimerUnit>('minutes');
  const [completionActionValues, setCompletionActionValues] = useState<
    Record<keyof BoardTimerCompletionActions, TimerCompletionActionValue>
  >(getDefaultCompletionActionValues);
  const [completionActionsEnabled, setCompletionActionsEnabled] = useState(false);
  const [isUpdatingTimer, setIsUpdatingTimer] = useState(false);

  const customTimerValueNumber = Number(customTimerValue);
  const customTimerDurationSeconds = customTimerValueNumber * TIMER_UNIT_SECONDS[customTimerUnit];
  const customTimerValueIsValid =
    Number.isInteger(customTimerValueNumber) && customTimerValueNumber > 0;
  const selectedTimerDurationSeconds =
    selectedTimerDuration === CUSTOM_TIMER_OPTION
      ? customTimerDurationSeconds
      : Number(selectedTimerDuration);
  const selectedTimerDurationIsValid =
    selectedTimerDuration === CUSTOM_TIMER_OPTION
      ? customTimerValueIsValid
      : QUICK_TIMER_OPTIONS.some((option) => option.seconds === selectedTimerDurationSeconds);
  const timerCompletionActionOptions = TIMER_COMPLETION_ACTION_OPTIONS.filter(
    (option) => hasVoting || option.key !== 'votingEnabled'
  );

  const handleClose = () => {
    if (!isUpdatingTimer) {
      onClose();
    }
  };

  const handleStartSelectedTimer = async () => {
    if (isUpdatingTimer || !selectedTimerDurationIsValid) {
      return;
    }

    setIsUpdatingTimer(true);

    try {
      const completionActions = completionActionsEnabled
        ? timerCompletionActionOptions.reduce<BoardTimerCompletionActions>((actions, option) => {
            const enabledValue = getCompletionActionEnabledValue(
              completionActionValues[option.key]
            );

            if (enabledValue !== null) {
              actions[option.key] = enabledValue;
            }

            return actions;
          }, {})
        : {};

      onBeforeStart?.();
      await onStartTimer(selectedTimerDurationSeconds, completionActions);
      setCompletionActionValues(getDefaultCompletionActionValues());
      setCompletionActionsEnabled(false);
      onClose();
    } finally {
      setIsUpdatingTimer(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          mt: 1,
          width: 400,
          maxWidth: 'calc(100vw - 32px)',
          borderRadius: 3,
          p: 2,
        },
      }}
    >
      <Stack spacing={2}>
        <Box component="h2" sx={{ m: 0, fontSize: '1rem', fontWeight: 700 }}>
          Set Timer
        </Box>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel id="board-timer-duration-label">Duration</InputLabel>
            <Select
              labelId="board-timer-duration-label"
              label="Duration"
              value={selectedTimerDuration}
              onChange={(event) => setSelectedTimerDuration(event.target.value)}
            >
              {QUICK_TIMER_OPTIONS.map((option) => (
                <MenuItem key={option.seconds} value={option.seconds.toString()}>
                  {option.label}
                </MenuItem>
              ))}
              <MenuItem value={CUSTOM_TIMER_OPTION}>Custom</MenuItem>
            </Select>
          </FormControl>
          {selectedTimerDuration === CUSTOM_TIMER_OPTION && (
            <Stack direction="row" spacing={1.5}>
              <TextField
                autoFocus
                label="Time"
                type="number"
                value={customTimerValue}
                onChange={(event) => setCustomTimerValue(event.target.value)}
                inputProps={{ min: 1, step: 1 }}
                error={customTimerValue.length > 0 && !customTimerValueIsValid}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ width: 120 }}>
                <InputLabel id="board-timer-custom-unit-label">Unit</InputLabel>
                <Select
                  labelId="board-timer-custom-unit-label"
                  label="Unit"
                  value={customTimerUnit}
                  onChange={(event) => setCustomTimerUnit(event.target.value as TimerUnit)}
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="hours">Hours</MenuItem>
                  <MenuItem value="days">Days</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </Stack>
        <Stack spacing={1.25}>
          <FormControlLabel
            control={
              <Switch
                checked={completionActionsEnabled}
                onChange={(event) => setCompletionActionsEnabled(event.target.checked)}
              />
            }
            label="Perform actions when the timer completes"
            sx={{
              alignItems: 'center',
              m: 0,
              '.MuiFormControlLabel-label': {
                color: '#3c4043',
                fontSize: '0.875rem',
                fontWeight: 700,
              },
            }}
          />
          <Collapse in={completionActionsEnabled} timeout="auto" unmountOnExit>
            <Stack spacing={1.25} sx={{ pt: 0.5 }}>
              {timerCompletionActionOptions.map((option) => (
                <TimerCompletionActionRow
                  key={option.key}
                  label={option.label}
                  value={completionActionValues[option.key]}
                  onChange={(value) =>
                    setCompletionActionValues((currentValues) => ({
                      ...currentValues,
                      [option.key]: value,
                    }))
                  }
                />
              ))}
            </Stack>
          </Collapse>
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button disabled={isUpdatingTimer} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isUpdatingTimer || !selectedTimerDurationIsValid}
            onClick={() => void handleStartSelectedTimer()}
          >
            Start
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};

export default BoardTimerSetupPopover;
